/**
 * View format and text size: the storage adapter.
 *
 * Two-phase, like `theme-store.js`. Phase one paints from `/app`'s local copy
 * the moment the uid is known; phase two reconciles with the shared settings
 * document. Waiting for the network before painting would mean an advisor on
 * "+4pt" reads one frame of 12px type on every load, which is the exact thing
 * the setting exists to prevent.
 */

import { readDoc, mergeDoc } from './firebase.js'
import { wrap } from './settings.js'
import {
  applyView,
  applyFontScale,
  normalizeView,
  normalizeScale,
  readLegacy,
  writeLegacy,
  resolveView,
  resolveScale,
  VIEW_KEY,
  SCALE_KEY,
} from './appearance.js'

/**
 * Phase one: paint what we can know without waiting.
 *
 * @param {string} uid
 * @returns {{view: 'original'|'expanded', scale: number}}
 */
export function applyCachedAppearance(uid) {
  const view = applyView(resolveView({ legacyLocal: readLegacy(uid, VIEW_KEY) }))
  const scale = applyFontScale(resolveScale({ legacyLocal: readLegacy(uid, SCALE_KEY) }))
  return { view, scale }
}

/**
 * Phase two: reconcile against the shared settings document.
 *
 * A failed read leaves what is already painted alone. The advisor's type size
 * changing on its own because a network call failed is worse than being one
 * round trip out of date.
 *
 * @param {string} uid
 * @returns {Promise<{view: 'original'|'expanded', scale: number}>}
 */
export async function syncAppearanceFromCloud(uid) {
  const cached = {
    view: resolveView({ legacyLocal: readLegacy(uid, VIEW_KEY) }),
    scale: resolveScale({ legacyLocal: readLegacy(uid, SCALE_KEY) }),
  }
  try {
    const settings = await readDoc(['users', uid, 'settings', 'main'])
    const view = resolveView({ settings, legacyLocal: readLegacy(uid, VIEW_KEY) })
    const scale = resolveScale({ settings, legacyLocal: readLegacy(uid, SCALE_KEY) })

    if (view !== cached.view) {
      applyView(view)
      writeLegacy(uid, VIEW_KEY, view)
    }
    if (scale !== cached.scale) {
      applyFontScale(scale)
      writeLegacy(uid, SCALE_KEY, scale)
    }
    return { view, scale }
  } catch (err) {
    console.error('appearance sync failed, keeping what is on screen', err)
    return cached
  }
}

/**
 * Paint first, then persist. The advisor asked for a visual change; making
 * them wait on a network write to see it is the wrong trade, and a failed
 * write must not mean a dead control.
 *
 * @param {string} uid @param {unknown} view
 * @returns {Promise<'original'|'expanded'>}
 */
export async function setView(uid, view) {
  const name = normalizeView(view)
  applyView(name)
  writeLegacy(uid, VIEW_KEY, name)
  announce(name)
  await save(uid, VIEW_KEY, name)
  return name
}

/** The event `setView` fires. Listen for it rather than polling the class. */
export const VIEW_EVENT = 'mbm:view'

/**
 * Say the view format changed.
 *
 * Since 19 Aug 2026 this control lives in the Settings modal, which opens on
 * top of whatever screen you were already on. "Original" and "Expanded" are
 * not a skin — the itinerary draws ONE page or ALL of them depending on which
 * you picked — so a body class is not enough on its own; the screen underneath
 * has to be told to redraw. Without this, switching the format from Settings
 * appears to do nothing until you navigate away and back.
 *
 * @param {'original'|'expanded'} name
 */
function announce(name) {
  const target = globalThis.document
  if (!target || typeof target.dispatchEvent !== 'function') return
  target.dispatchEvent(new CustomEvent(VIEW_EVENT, { detail: { view: name } }))
}

/**
 * @param {string} uid @param {unknown} level
 * @returns {Promise<number>}
 */
export async function setFontScale(uid, level) {
  const scale = normalizeScale(level)
  applyFontScale(scale)
  /* `/app` stores this one as a STRING of an integer (app.html:7843). Writing
     a number would be read back by `parseInt` fine, but the settings sync
     compares raw values — a type change is a spurious difference forever. */
  writeLegacy(uid, SCALE_KEY, String(scale))
  await save(uid, SCALE_KEY, String(scale))
  return scale
}

/**
 * @param {string} uid @param {string} key @param {string} value
 * @returns {Promise<boolean>}
 */
async function save(uid, key, value) {
  try {
    await mergeDoc(['users', uid, 'settings', 'main'], {
      [key]: wrap(value, new Date().toISOString()),
    })
    return true
  } catch (err) {
    console.error(`${key} save failed; applied locally but will not follow you`, err)
    return false
  }
}
